import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import PageShell, { Section } from "@/components/site/PageShell";
import { Cards, Note, Prose, Table } from "@/components/site/ui";
import { Inline } from "@/components/site/Renderer";
import { BASE, MAX, CRITERIA, EXAMPLES } from "@/content/pricing";
import {
  ABOUT, AUDIENCES, BRAND, CAREERS, COMMUNITY, CONTACTS,
  EXPERTS, FEATURES, INTEGRATIONS, MANIFESTO, PARTNERS, PRESS, PRICING,
  ROADMAP, SUPPORT_TIERS, FEATURE_DETAILS,
} from "@/content/pages";

/* ---------------- Tarifs ---------------- */
export function Pricing() {
  return (
    <PageShell eyebrow={PRICING.eyebrow} title={PRICING.title} lead={PRICING.lead}
      actions={<Link to="/coach" className="button button-dark" data-testid="pricing-coach">Voir la méthode<ArrowRight size={13} /></Link>}>
      <Section><Note kind="info">{PRICING.notice}</Note></Section>

      <Section title={`Base : ${BASE} €`} lead="Une séance d'une heure, module standard, sur un jeu hors FPS, sans préparation à la compétition. Chaque critère ci-dessous s'ajoute à ce montant.">
        <div className="price-criteria">
          {CRITERIA.map(c => (
            <div key={c.key} className="price-criterion" data-testid={`price-${c.key}`}>
              <div className="price-criterion-head">
                <h3>{c.label}</h3>
                <p>{c.why}</p>
              </div>
              <table className="kv-table">
                <thead><tr><th>Option</th><th>Effet sur le prix</th></tr></thead>
                <tbody>
                  {c.options.map(o => (
                    <tr key={o.label}>
                      <td>{o.label}{o.note && <small style={{ display: "block", color: "#8b9382", fontWeight: 400, marginTop: 3 }}>{o.note}</small>}</td>
                      <td style={{ whiteSpace: "nowrap", fontWeight: o.delta ? 550 : 400 }}>
                        {o.delta ? `+ ${o.delta} €` : "compris"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <p className="price-total">
          Le total va donc de <strong>{BASE} €</strong> pour la séance la plus simple
          à <strong>{MAX} €</strong> quand tous les critères sont au maximum.
        </p>
      </Section>

      <Section title="Trois exemples" lead="Calculés à partir du tableau ci-dessus, pas arrondis pour faire joli.">
        <div className="card-grid">
          {EXAMPLES.map(e => (
            <div key={e.title} className="info-card">
              <h3>{e.title}</h3>
              <p>{e.detail}</p>
              <div className="price-example">{e.price} €</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Questions fréquentes">
        <div className="faq-list" style={{ maxWidth: 720 }}>
          {PRICING.faq.map(f => (
            <details key={f.q}><summary><span>{f.q}</span><Minus size={14} /></summary><p>{f.a}</p></details>
          ))}
        </div>
      </Section>
    </PageShell>
  );
}

/* ---------------- Fonctionnalités ---------------- */
export function Features() {
  return (
    <PageShell eyebrow={FEATURES.eyebrow} title={FEATURES.title} lead={FEATURES.lead}>
      <Section title="Disponible aujourd'hui" lead="Ce que vous pouvez utiliser dès maintenant.">
        <Cards items={FEATURES.live} />
      </Section>
      <Section title="En construction" lead="Annoncé, pas encore livré. Le détail et l'ordre figurent sur la feuille de route.">
        <Cards items={FEATURES.building} />
        <div style={{ marginTop: 22 }}>
          <Link to="/roadmap" className="landing-link">Voir la feuille de route<ArrowRight size={13} /></Link>
        </div>
      </Section>
    </PageShell>
  );
}

/* ---------------- Pour qui ---------------- */
export function Audience() {
  const { segment } = useParams();
  const data = AUDIENCES[segment];
  if (!data) return <Navigate to="/fonctionnalites" replace />;
  return (
    <PageShell eyebrow={data.eyebrow} title={data.title} lead={data.lead}
      actions={<Link to={data.cta.to} className="button button-dark" data-testid="audience-cta">{data.cta.label}<ArrowRight size={13} /></Link>}>
      <Section><Cards items={data.points} /></Section>
      {data.notice && <Section><Note kind="info">{data.notice}</Note></Section>}
    </PageShell>
  );
}

/* ---------------- À propos ---------------- */
export function About() {
  return (
    <PageShell eyebrow={ABOUT.eyebrow} title={ABOUT.title} lead={ABOUT.lead}>
      {ABOUT.sections.map(s => (
        <Section key={s.heading} title={s.heading}>
          <Prose>{s.body.map((p, i) => <p key={i}>{p}</p>)}</Prose>
        </Section>
      ))}
      <Section>
        <Link to="/manifeste" className="landing-link">Lire le manifeste<ArrowRight size={13} /></Link>
      </Section>
    </PageShell>
  );
}

/* ---------------- Manifeste ---------------- */
export function Manifesto() {
  return (
    <PageShell eyebrow={MANIFESTO.eyebrow} title={MANIFESTO.title} lead={MANIFESTO.lead}>
      {MANIFESTO.beliefs.map((b, i) => (
        <Section key={b.title} title={`${String(i + 1).padStart(2, "0")} — ${b.title}`}>
          <Prose><p>{b.text}</p></Prose>
        </Section>
      ))}
    </PageShell>
  );
}

/* ---------------- Carrières ---------------- */
export function Careers() {
  return (
    <PageShell eyebrow={CAREERS.eyebrow} title={CAREERS.title} lead={CAREERS.lead}>
      <Section><Note>{CAREERS.notice}</Note></Section>
      <Section title="Comment nous travaillons">
        <Cards items={CAREERS.values.map(v => ({ title: v.title, text: v.text }))} />
      </Section>
      <Section title="Le processus">
        <Prose><ol>{CAREERS.process.map((p, i) => <li key={i}>{p}</li>)}</ol></Prose>
      </Section>
      <Section title="Ce que nous offrons">
        <div className="pill-row">{CAREERS.benefits.map(b => <span key={b} className="pill">{b}</span>)}</div>
      </Section>
      <Section>
        <a className="button button-dark" href="mailto:bonjour@performcustom.com?subject=Candidature%20spontan%C3%A9e" data-testid="careers-apply">
          Candidature spontanée<ArrowRight size={13} />
        </a>
      </Section>
    </PageShell>
  );
}

/* ---------------- Presse ---------------- */
export function Press() {
  return (
    <PageShell eyebrow={PRESS.eyebrow} title={PRESS.title} lead={PRESS.lead}>
      <Section title="Description de l'entreprise" lead="Réutilisable tel quel dans vos articles.">
        <Prose><p>{PRESS.boilerplate}</p></Prose>
      </Section>
      <Section title="En bref"><Table rows={PRESS.facts} /></Section>
      <Section title="Éléments de marque"><Cards items={PRESS.assets.map(a => ({ title: a.name, text: a.text, to: a.to, linkLabel: "Voir les règles" }))} /></Section>
      <Section title="Contact presse">
        <Prose><p>Écrivez à <a href="mailto:presse@performcustom.com">presse@performcustom.com</a>. Nous répondons sous deux jours ouvrés.</p></Prose>
      </Section>
    </PageShell>
  );
}

/* ---------------- Partenaires ---------------- */
export function Partners() {
  return (
    <PageShell eyebrow={PARTNERS.eyebrow} title={PARTNERS.title} lead={PARTNERS.lead}
      actions={<Link to={PARTNERS.cta.to} className="button button-dark" data-testid="partners-cta">{PARTNERS.cta.label}<ArrowRight size={13} /></Link>}>
      <Section><Cards items={PARTNERS.kinds} /></Section>
      <Section><Note>{PARTNERS.notice}</Note></Section>
    </PageShell>
  );
}

/* ---------------- Experts ---------------- */
export function Experts() {
  return (
    <PageShell eyebrow={EXPERTS.eyebrow} title={EXPERTS.title} lead={EXPERTS.lead}
      actions={<Link to={EXPERTS.cta.to} className="button button-dark">{EXPERTS.cta.label}<ArrowRight size={13} /></Link>}>
      <Section><Cards items={EXPERTS.services} /></Section>
      <Section><Note>{EXPERTS.notice}</Note></Section>
    </PageShell>
  );
}

/* ---------------- Communauté ---------------- */
export function Community() {
  return (
    <PageShell eyebrow={COMMUNITY.eyebrow} title={COMMUNITY.title} lead={COMMUNITY.lead}>
      <Section>
        <div className="card-grid">
          {COMMUNITY.channels.map(c => (
            <div key={c.title} className="info-card">
              <h3>{c.title}{!c.available && <span className="pill" style={{ marginLeft: 8, fontSize: 10 }}>Bientôt</span>}</h3>
              <p>{c.text}</p>
              {c.available && c.to && <Link to={c.to}>Y aller<ArrowRight size={12} /></Link>}
            </div>
          ))}
        </div>
      </Section>
      <Section><Note>{COMMUNITY.notice}</Note></Section>
    </PageShell>
  );
}

/* ---------------- Feuille de route ---------------- */
export function Roadmap() {
  return (
    <PageShell eyebrow={ROADMAP.eyebrow} title={ROADMAP.title} lead={ROADMAP.lead}>
      <Section>
        <div className="roadmap">
          {ROADMAP.columns.map(col => (
            <div key={col.key} className={`roadmap-col is-${col.key}`}>
              <h3>{col.label}<span>{col.items.length}</span></h3>
              {col.items.map(item => (
                <div key={item.title} className="roadmap-item">
                  <strong>{item.title}</strong>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>
      <Section><Note kind="info">{ROADMAP.vote}</Note></Section>
    </PageShell>
  );
}

/* ---------------- Intégrations ---------------- */
export function Integrations() {
  return (
    <PageShell eyebrow={INTEGRATIONS.eyebrow} title={INTEGRATIONS.title} lead={INTEGRATIONS.lead}>
      <Section><Note>{INTEGRATIONS.notice}</Note></Section>
      <Section>
        <div className="card-grid">
          {INTEGRATIONS.items.map(it => (
            <div key={it.name} className={`info-card${it.available ? "" : " is-muted"}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <h3>{it.name}</h3>
                <span className={`pill${it.available ? " is-ok" : ""}`} style={{ fontSize: 10 }}>{it.available ? "Disponible" : "Prévu"}</span>
              </div>
              <p style={{ marginTop: 8 }}>{it.text}</p>
              <small style={{ display: "block", marginTop: 10, fontSize: 11, color: "#97a08f" }}>{it.category}</small>
              {it.available && it.to && <Link to={it.to}>Documentation<ArrowRight size={12} /></Link>}
            </div>
          ))}
        </div>
      </Section>
    </PageShell>
  );
}

/* ---------------- Marque ---------------- */
export function Brand() {
  return (
    <PageShell eyebrow={BRAND.eyebrow} title={BRAND.title} lead={BRAND.lead}>
      <Section title="Règles d'usage">
        <div className="card-grid cols-2">
          <div className="info-card">
            <h3>À faire</h3>
            <ul className="rule-list is-do">{BRAND.rules.do.map(r => <li key={r}><Check size={13} />{r}</li>)}</ul>
          </div>
          <div className="info-card">
            <h3>À éviter</h3>
            <ul className="rule-list is-dont">{BRAND.rules.dont.map(r => <li key={r}><Minus size={13} />{r}</li>)}</ul>
          </div>
        </div>
      </Section>
      <Section title="Couleurs">
        <div className="swatches">
          {BRAND.palette.map(c => (
            <div key={c.hex} className="swatch">
              <i style={{ background: c.hex }} />
              <strong>{c.name}</strong><code>{c.hex}</code><small>{c.use}</small>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Typographie">
        <Table head={["Police", "Usage", "Graisses"]} rows={BRAND.type.map(t => [t.name, t.use, t.weights])} />
      </Section>
    </PageShell>
  );
}


/* ---------------- Contact ---------------- */
export function Contact() {
  return (
    <PageShell eyebrow="Contact" title="Nous écrire." lead="Une adresse par sujet : votre message arrive directement à la bonne personne.">
      <Section>
        <div className="card-grid">
          {CONTACTS.map(c => (
            <div key={c.key} className="info-card">
              <h3>{c.title}</h3>
              <p>{c.text}</p>
              <a href={`mailto:${c.email}`} style={{ display: "inline-block", marginTop: 12, fontSize: 12.5, color: "var(--green)", textDecoration: "underline" }} data-testid={`contact-${c.key}`}>{c.email}</a>
              {c.note && <small style={{ display: "block", marginTop: 6, fontSize: 11, color: "#97a08f" }}>{c.note}</small>}
              {c.to && <Link to={c.to}>En savoir plus<ArrowRight size={12} /></Link>}
            </div>
          ))}
        </div>
      </Section>
      <Section title="Niveaux de support" lead="Nous n'affichons pas d'engagement que nous ne pouvons pas tenir.">
        <div className="card-grid">
          {SUPPORT_TIERS.map(t => (
            <div key={t.name} className="info-card">
              <h3>{t.name}</h3>
              <ul className="rule-list is-do" style={{ marginTop: 10 }}>{t.items.map(i => <li key={i}><Check size={13} />{i}</li>)}</ul>
              {t.note && <p style={{ marginTop: 12, fontSize: 12 }}><Inline text={t.note} /></p>}
            </div>
          ))}
        </div>
      </Section>
    </PageShell>
  );
}

/* ---------------- Témoignages ---------------- */
export function Testimonials() {
  return (
    <PageShell eyebrow="Témoignages" title="Pas encore d'histoires à raconter."
      lead="Perform vient d'ouvrir. Nous préférons une page vide à des témoignages inventés.">
      <Section>
        <Prose>
          <p>
            Beaucoup de sites affichent dès le premier jour des citations élogieuses et des logos de clients
            qui n'ont jamais utilisé le produit. Nous ne le ferons pas.
          </p>
          <p>
            Cette page se remplira avec de vrais retours de joueurs et de coachs, nommés et vérifiables,
            à mesure que les premières séances auront lieu.
          </p>
          <p>
            En attendant, vous pouvez juger notre sérieux sur ce qui est vérifiable dès aujourd'hui :
            notre <Link to="/trust-center">Trust Center</Link>, notre <Link to="/securite">page sécurité</Link>,
            notre <Link to="/roadmap">feuille de route</Link> et notre <Link to="/statut">état des services</Link>.
          </p>
        </Prose>
      </Section>
      <Section>
        <Link to="/auth" className="button button-dark">Être parmi les premiers<ArrowRight size={13} /></Link>
      </Section>
    </PageShell>
  );
}

/* ---------------- Détail d'une fonctionnalité ---------------- */
export function FeatureDetail() {
  const { feature } = useParams();
  const data = FEATURE_DETAILS[feature];
  if (!data) return <Navigate to="/fonctionnalites" replace />;
  return (
    <PageShell eyebrow={data.eyebrow} title={data.title} lead={data.lead}
      actions={<>
        <Link to="/coach" className="button button-dark">Voir la méthode<ArrowRight size={13} /></Link>
        <Link to="/roadmap" className="button button-outline">Quand ?</Link>
      </>}>
      <Section>
        <Note>
          Cette fonctionnalité est en construction : elle est annoncée, pas encore livrée.
          Son avancement figure sur la <Link to="/roadmap">feuille de route</Link>.
        </Note>
      </Section>
      <Section title="Comment ça marchera"><Cards items={data.how} /></Section>
      <Section title="Pourquoi"><Prose><p>{data.why}</p></Prose></Section>
      {data.notice && <Section><Note kind="info"><Inline text={data.notice} /></Note></Section>}
    </PageShell>
  );
}
